/**
 * AttributeReconciliationTable
 *
 * Shows ML-detected attributes alongside the registered (ground-truth) values
 * for color, vehicle_type, and plate_text, with a mismatch cue for each row.
 * brand/model are rendered as registered-only context.
 * direction is an editable Select (entry|exit) defaulting to the ml value.
 *
 * Used in LprVerificationDrawer when a vehicle is linked/selected.
 * The mismatch highlight is COSMETIC — no logic depends on it.
 */
import { useTranslation } from 'react-i18next'
import { CheckCircle, AlertTriangle } from 'lucide-react'
import { Badge } from '@/components/ui/Badge'
import { Select } from '@/components/ui/Select'
import type { LprEvent, LprEventDirection, Vehicle } from '@/types/parking'

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

/** ML-detected attributes from an LprEvent — subset forwarded to this table. */
export interface MlAttrs {
  color: LprEvent['color']
  vehicle_type: LprEvent['vehicle_type']
  plate_text: LprEvent['plate_text']
  direction: LprEvent['direction']
}

export interface AttributeReconciliationTableProps {
  /** ML-detected attributes (from the LprEvent). */
  mlAttrs: MlAttrs
  /** The linked registered vehicle (ground truth). */
  registeredVehicle: Vehicle
  /** Currently selected operator direction (controlled). */
  direction: LprEventDirection
  /** Called when the operator selects a different direction. */
  onDirectionChange: (value: LprEventDirection) => void
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function isMismatch(a: string | null | undefined, b: string | null | undefined): boolean {
  if (!a || !b) return false
  return a.toLowerCase() !== b.toLowerCase()
}

// ---------------------------------------------------------------------------
// Sub-components
// ---------------------------------------------------------------------------

function MatchIcon({ match }: { match: boolean }) {
  const { t } = useTranslation('parking')
  if (match) {
    return (
      <span className="inline-flex items-center gap-0.5 shrink-0">
        <CheckCircle className="w-3.5 h-3.5 text-success" aria-hidden="true" />
        <span className="sr-only">{t('lpr.reconciliation_table.match')}</span>
      </span>
    )
  }
  return (
    <span className="inline-flex items-center gap-0.5 shrink-0">
      <AlertTriangle className="w-3.5 h-3.5 text-warning" aria-hidden="true" />
      <span className="sr-only">{t('lpr.reconciliation_table.mismatch')}</span>
    </span>
  )
}

function ValueCell({
  ml,
  registered,
  mono = false,
}: {
  ml: string | null | undefined
  registered: string | null | undefined
  mono?: boolean
}) {
  const { t } = useTranslation('parking')
  const match = !isMismatch(ml, registered)
  const badgeVariant = match ? 'ghost' : 'warning'

  return (
    <div className="min-w-0 flex flex-wrap items-center gap-x-2 gap-y-1">
      {/* ML value */}
      <span className={`text-sm text-base-content/60 ${mono ? 'font-mono' : ''}`}>
        {ml ?? '—'}
      </span>
      <span className="text-xs text-base-content/30" aria-hidden="true">→</span>
      {/* Registered value as badge */}
      <Badge
        variant={badgeVariant}
        size="sm"
        outline={!match}
        className={mono ? 'font-mono' : ''}
      >
        {registered ?? '—'}
      </Badge>
      <MatchIcon match={match} />
      <span className="sr-only">
        {match
          ? t('lpr.reconciliation_table.match')
          : t('lpr.reconciliation_table.mismatch')}
      </span>
    </div>
  )
}

// ---------------------------------------------------------------------------
// Main component
// ---------------------------------------------------------------------------

export function AttributeReconciliationTable({
  mlAttrs,
  registeredVehicle,
  direction,
  onDirectionChange,
}: AttributeReconciliationTableProps) {
  const { t } = useTranslation('parking')

  const directionOptions = [
    { value: 'entry', label: t('lpr.directions.entry') },
    { value: 'exit', label: t('lpr.directions.exit') },
  ]

  return (
    <section
      aria-label={t('lpr.reconciliation_table.section_label')}
      className="space-y-1"
    >
      {/* Column header row */}
      <div
        className="grid grid-cols-[auto_1fr] gap-x-3 text-xs font-semibold uppercase tracking-wide text-base-content/40 pb-1"
        aria-hidden="true"
      >
        <span>{t('lpr.reconciliation_table.attribute')}</span>
        <span>{t('lpr.reconciliation_table.ml_to_registered')}</span>
      </div>

      {/* Attribute rows */}
      <dl className="grid grid-cols-[auto_1fr] gap-x-3 gap-y-2">
        {/* Color */}
        <dt className="text-sm text-base-content/50 self-center whitespace-nowrap">
          {t('lpr.verification_drawer.color')}
        </dt>
        <dd>
          <ValueCell ml={mlAttrs.color} registered={registeredVehicle.color} />
        </dd>

        {/* Vehicle type */}
        <dt className="text-sm text-base-content/50 self-center whitespace-nowrap">
          {t('lpr.verification_drawer.vehicle_type')}
        </dt>
        <dd>
          <ValueCell
            ml={mlAttrs.vehicle_type ? t(`vehicles.types.${mlAttrs.vehicle_type}`) : null}
            registered={t(`vehicles.types.${registeredVehicle.vehicle_type}`)}
          />
        </dd>

        {/* Plate text */}
        <dt className="text-sm text-base-content/50 self-center whitespace-nowrap">
          {t('lpr.plate_text')}
        </dt>
        <dd>
          <ValueCell
            ml={mlAttrs.plate_text}
            registered={registeredVehicle.license_plate}
            mono
          />
        </dd>

        {/* Brand / Model — registered-only context */}
        {(registeredVehicle.brand || registeredVehicle.model) && (
          <>
            <dt className="text-sm text-base-content/50 self-center whitespace-nowrap">
              {t('lpr.reconciliation_table.brand_model')}
            </dt>
            <dd className="min-w-0 text-sm text-base-content/70 break-words">
              {[registeredVehicle.brand, registeredVehicle.model].filter(Boolean).join(' ')}
            </dd>
          </>
        )}

        {/* Direction — editable */}
        <dt className="text-sm text-base-content/50 self-center whitespace-nowrap">
          {t('lpr.direction')}
        </dt>
        <dd className="min-w-0 bg-base-200/40 rounded-md px-2 py-1">
          <Select
            className="w-full"
            size="sm"
            options={directionOptions}
            value={direction}
            onChange={(e) => onDirectionChange(e.target.value as LprEventDirection)}
            aria-label={t('lpr.reconciliation_table.direction_label')}
            data-testid="direction-select"
          />
        </dd>
      </dl>
    </section>
  )
}
